import { useAuth } from "@/domains/shared/hooks/useAuth";
import { Roles } from "@/domains/shared/models/roles";
import { needsEmailVerification } from "@/domains/shared/services/emailVerification";
import { isAssinaturaConversionState, requiresPaymentBeforeAccess, shouldAutoOpenPendingCheckout } from "@/domains/subscription/accountAccess";
import { consumeAutoOpenCheckout, getPlanIntent, setPlanIntent } from "@/domains/subscription/planIntent";
import { isPaidPlan, isTrialPlan, type SubscriptionPlanId } from "@/domains/subscription/plans";
import { useSubscriptionCheckout } from "@/domains/subscription/SubscriptionCheckoutContext";
import { useEffect } from "react";
import { useLocation } from "react-router";

export function PendingSubscriptionCheckoutEffect() {
    const { user } = useAuth();
    const { open, openCheckout, skipPendingCheckoutAutoOpenRef } = useSubscriptionCheckout();
    const location = useLocation();

    useEffect(() => {
        if (!user || user.role !== Roles.Therapist) {
            return;
        }

        if (needsEmailVerification(user)) {
            return;
        }

        if (open) {
            return;
        }

        if (requiresPaymentBeforeAccess(user)) {
            if (skipPendingCheckoutAutoOpenRef.current) {
                if (!shouldAutoOpenPendingCheckout(location.pathname)) {
                    skipPendingCheckoutAutoOpenRef.current = false;
                }
                return;
            }

            if (!shouldAutoOpenPendingCheckout(location.pathname)) {
                return;
            }

            if (user.pendingPaidPlanId && isPaidPlan(user.pendingPaidPlanId)) {
                setPlanIntent(user.pendingPaidPlanId as SubscriptionPlanId);
            }

            const checkoutPlan = user.pendingPaidPlanId as SubscriptionPlanId;

            openCheckout(checkoutPlan, {
                mode: "checkout",
                initialStep: "method",
                nonDismissable: true,
            });
            return;
        }

        if (isAssinaturaConversionState({
            role: user.role,
            subscriptionPlan: user.subscriptionPlan,
            pendingPaidPlanId: user.pendingPaidPlanId,
            activeClinicId: user.activeClinicId,
            referralCheckoutPending: user.referralCheckoutPending,
            trialDaysRemaining: user.trialDaysRemaining,
            subscriptionEndsAt: user.subscriptionEndsAt,
        })) {
            return;
        }

        if (!isTrialPlan(user.subscriptionPlan)) {
            return;
        }

        const intent = getPlanIntent();
        if (!intent || !consumeAutoOpenCheckout()) {
            return;
        }

        openCheckout(intent);
    }, [user, openCheckout, open, location.pathname]);

    return null;
}
